import { Fragment, useCallback, useEffect, useState } from "react";
import {
  fetchFoodOsmRefreshSchedulerRuns,
  fetchFoodOsmRefreshSchedulerStatus,
  postFoodOsmRefreshSchedulerDisable,
  postFoodOsmRefreshSchedulerEnable,
  postFoodOsmRefreshSchedulerRunNow,
  postFoodOsmRefreshSchedulerRunNowDryRun,
} from "../../../../api/foodApi";
import { formatRelativeFreshness } from "../../../../utils/foodFormatters";

function parseSchedulerError(error) {
  return error?.body?.message || error?.message || "Scheduler action failed.";
}

function formatSummary(summary = {}) {
  const parts = [];
  if (summary.osmIngest?.fetchedCount != null) {
    parts.push(`fetched ${summary.osmIngest.fetchedCount}`);
  }
  if (summary.osmIngestDryRun?.fetchedCount != null) {
    parts.push(`dry-run fetched ${summary.osmIngestDryRun.fetchedCount}`);
  }
  if (summary.normalizationDryRun?.recordsNormalized != null) {
    parts.push(`norm proposed ${summary.normalizationDryRun.recordsNormalized}`);
  }
  if (summary.reviewQueueCount != null) {
    parts.push(`review queue ${summary.reviewQueueCount}`);
  }
  return parts.length ? parts.join(" · ") : "—";
}

const FoodOsmRefreshSchedulerSection = ({ canManage = false }) => {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [expandedRunId, setExpandedRunId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [schedulerStatus, runHistory] = await Promise.all([
        fetchFoodOsmRefreshSchedulerStatus(),
        fetchFoodOsmRefreshSchedulerRuns({ limit: 10 }),
      ]);
      setStatus(schedulerStatus);
      setRuns(runHistory.runs || []);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(parseSchedulerError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleEnable = async () => {
    if (!canManage || busy) return;
    try {
      setBusy(true);
      await postFoodOsmRefreshSchedulerEnable({});
      await load();
    } catch (error) {
      setErrorMessage(parseSchedulerError(error));
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (!canManage || busy) return;
    if (!window.confirm("Disable the weekly OSM refresh scheduler?")) return;
    try {
      setBusy(true);
      await postFoodOsmRefreshSchedulerDisable({});
      await load();
    } catch (error) {
      setErrorMessage(parseSchedulerError(error));
    } finally {
      setBusy(false);
    }
  };

  const handleDryRun = async () => {
    if (!canManage || busy) return;
    try {
      setBusy(true);
      await postFoodOsmRefreshSchedulerRunNowDryRun({});
      await load();
    } catch (error) {
      setErrorMessage(parseSchedulerError(error));
    } finally {
      setBusy(false);
    }
  };

  const handleRunNow = async () => {
    if (!canManage || busy) return;
    if (
      !window.confirm(
        "You are about to run the weekly OSM refresh now. This may ingest OSM supermarkets and run normalization dry run. Continue?"
      )
    ) {
      return;
    }
    try {
      setBusy(true);
      await postFoodOsmRefreshSchedulerRunNow({ confirm: true });
      await load();
    } catch (error) {
      setErrorMessage(parseSchedulerError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="food-osm-refresh-scheduler"
      className="panel-card food-osm-refresh-scheduler"
      aria-labelledby="food-osm-refresh-scheduler-title"
    >
      <div className="food-manual-refresh-playbook__header">
        <div>
          <h2 id="food-osm-refresh-scheduler-title">Weekly OSM Refresh Scheduler</h2>
          <p className="muted">
            Conservative weekly refresh for <code>OSM_FOOD_POIS</code>. Disabled by default. Does
            not import chain files, apply reconciliation, or recalculate access areas.
          </p>
        </div>
        {status ? (
          <span
            className={`food-manual-refresh-overall food-manual-refresh-overall--${status.enabled ? "current" : "ready"}`}
          >
            {status.enabled ? "Enabled" : "Disabled"}
          </span>
        ) : null}
      </div>

      <p className="food-osm-refresh-scheduler__warning muted" role="note">
        Safe defaults: scheduler off, normalization apply off, no chain import, no reconciliation
        apply, no access recalculation apply.
      </p>

      {errorMessage ? (
        <p className="food-dashboard__error" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {loading && !status ? (
        <p className="muted">Loading scheduler status…</p>
      ) : status ? (
        <>
          <div className="food-comparison-summary-cards">
            <div className="food-comparison-summary-cards__card">
              <span className="muted">Cron</span>
              <strong>{status.cronExpression}</strong>
              <span>{status.timezone}</span>
            </div>
            <div className="food-comparison-summary-cards__card">
              <span className="muted">Next run</span>
              <strong>{formatRelativeFreshness(status.nextRunAt)}</strong>
              <span>{status.sourceKey}</span>
            </div>
            <div className="food-comparison-summary-cards__card">
              <span className="muted">Last run</span>
              <strong>{formatRelativeFreshness(status.lastRunAt)}</strong>
              <span>{status.lastRunStatus || "—"}</span>
            </div>
            <div className="food-comparison-summary-cards__card">
              <span className="muted">Safe normalization apply</span>
              <strong>{status.applySafeNormalization ? "On" : "Off"}</strong>
              <span>
                Access dry run: {status.runAccessRecalcDryRun ? "On" : "Off"}
              </span>
            </div>
          </div>

          {(status.warnings?.length || status.errors?.length) ? (
            <ul className="food-manual-refresh-playbook__messages">
              {status.warnings?.map((warning) => (
                <li key={warning} className="food-manual-refresh-playbook__warning">
                  {warning}
                </li>
              ))}
              {status.errors?.map((entry) => (
                <li key={entry.message || entry} className="food-manual-refresh-playbook__error">
                  {entry.message || entry}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="food-normalization-page__control-row">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!canManage || busy || status.enabled}
              onClick={handleEnable}
            >
              Enable scheduler
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={!canManage || busy || !status.enabled}
              onClick={handleDisable}
            >
              Disable scheduler
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={!canManage || busy}
              onClick={handleDryRun}
            >
              Run scheduler dry run now
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={!canManage || busy}
              onClick={handleRunNow}
            >
              Run scheduler now
            </button>
          </div>

          <div className="food-manual-refresh-playbook__history">
            <h3>Scheduled run history</h3>
            {runs.length ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Triggered by</th>
                    <th>Dry run</th>
                    <th>Summary</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <Fragment key={run.id}>
                      <tr>
                        <td>{run.status}</td>
                        <td>{formatRelativeFreshness(run.startedAt)}</td>
                        <td>{run.durationMs != null ? `${run.durationMs} ms` : "—"}</td>
                        <td>{run.triggeredBy}</td>
                        <td>{run.dryRun ? "Yes" : "No"}</td>
                        <td>{formatSummary(run.summary)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() =>
                              setExpandedRunId((current) => (current === run.id ? null : run.id))
                            }
                          >
                            {expandedRunId === run.id ? "Hide" : "Details"}
                          </button>
                        </td>
                      </tr>
                      {expandedRunId === run.id ? (
                        <tr>
                          <td colSpan={7}>
                            <pre className="food-normalization-page__summary">
                              {JSON.stringify(run, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted">No scheduled OSM refresh runs recorded yet.</p>
            )}
          </div>
        </>
      ) : (
        <p className="muted">Unable to load scheduler status.</p>
      )}
    </section>
  );
};

export default FoodOsmRefreshSchedulerSection;
